import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause } from 'lucide-react';
import { PremiumGate } from '@/components/PremiumGate';
import { useEntitlements } from '@/hooks/useEntitlements';

const FractalBreathTree = () => {
  const navigate = useNavigate();
  const { hasAccess } = useEntitlements();
  const canvasRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [growth, setGrowth] = useState(0);
  const animationRef = useRef(null);
  const directionRef = useRef(1);
  const lastTimeRef = useRef(null);

  useEffect(() => {
    if (!isActive) return;

    // Full breathing cycle (inhale + exhale) takes ~8 seconds, regardless of
    // display refresh rate, since the step is scaled by elapsed real time.
    const HALF_CYCLE_MS = 4000;
    lastTimeRef.current = null;

    const animate = (timestamp) => {
      if (lastTimeRef.current === null) lastTimeRef.current = timestamp;
      const delta = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      setGrowth(prev => {
        const next = prev + directionRef.current * (delta / HALF_CYCLE_MS);
        if (next >= 1) directionRef.current = -1;
        if (next <= 0) directionRef.current = 1;
        return Math.max(0, Math.min(1, next));
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isActive]);

  useEffect(() => {
    if (!canvasRef.current) return;
    drawTree();
  }, [growth]);

  const drawTree = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = 600;
    canvas.height = 600;

    ctx.fillStyle = '#FDFBF7';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const drawBranch = (x, y, length, angle, depth) => {
      if (depth === 0 || length < 2) return;

      const endX = x + length * Math.cos(angle);
      const endY = y + length * Math.sin(angle);

      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(endX, endY);
      ctx.strokeStyle = `hsl(${120 - depth * 15}, 70%, ${40 + depth * 5}%)`;
      ctx.lineWidth = depth * 1.5;
      ctx.stroke();

      const newLength = length * (0.67 + growth * 0.1);
      const angleSpread = 0.5 + growth * 0.3;

      drawBranch(endX, endY, newLength, angle - angleSpread, depth - 1);
      drawBranch(endX, endY, newLength, angle + angleSpread, depth - 1);
    };

    const maxDepth = Math.floor(4 + growth * 4);
    drawBranch(300, 550, 100 + growth * 50, -Math.PI / 2, maxDepth);
  };

  if (!hasAccess('apps_premium')) {
    return <PremiumGate appName="Fractal Breath Tree" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E8F5E9] to-[#C8E6C9] relative overflow-hidden">
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        <header className="flex justify-between items-center mb-12">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-4xl font-bold text-[#2D3748]">Fractal Breath Tree</h1>
          
          <button
            data-testid="toggle-breathing"
            onClick={() => setIsActive(!isActive)}
            className="bg-gradient-to-r from-[#66BB6A] to-[#90EE90] text-white px-6 py-3 rounded-full font-semibold hover:-translate-y-1 transition-all shadow-lg flex items-center gap-2"
          >
            {isActive ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            {isActive ? 'Stop' : 'Start'}
          </button>
        </header>

        <div className="flex items-center justify-center">
          <div className="bg-white rounded-3xl p-8 shadow-2xl border border-slate-100">
            <canvas ref={canvasRef} className="max-w-full" />
            <p className="text-center text-2xl font-bold mt-6 text-[#2D3748]">
              {directionRef.current === 1 ? 'Breathe In' : 'Breathe Out'}
            </p>
          </div>
        </div>

        <div className="mt-12 bg-white rounded-2xl p-6 text-center shadow-lg">
          <p className="text-[#4A5568] text-lg">
            Watch the fractal tree grow and shrink with your breath. Notice the recursive patterns emerging with each cycle.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FractalBreathTree;